/**
 * Cross-widget event topics published by ws-inv-facets nested mode.
 *
 * Bus: window.DDC.pubsub.publish — same bus as DDC.InvData.publishEvent.
 * ws-inv-filters subscribers register via DDC.pubsub.subscribe, which is
 * the channel both buses ultimately deliver on.
 *
 * Emission order on mobile X close (from handleCloseModal):
 *   1. ws-inv-facets/nested-panel-closed (synchronous, before unmount)
 *   2. ws-inv-filters/close-filters (synchronous, asks parent to close)
 *
 * The parent runs its existing close flow in response to (2): exit
 * animation → setModalShow(false) → its own useEffect emits the
 * post-state ws-inv-filters/modal-hide, which this widget self-subscribes
 * to in Widget.jsx and uses to tear down the portal.
 */

/**
 * Fired when a user enters a nested detail panel from the initial list.
 * @event ws-inv-facets/nested-panel-open
 * @payload { facetId: string } — the active facet's id (or groupId for
 *   multi-facet groups).
 */
export const NESTED_PANEL_OPEN = 'ws-inv-facets/nested-panel-open';

/**
 * Fired when the user navigates back to the initial list, dismisses via
 * the mobile X close icon, or presses Escape inside a detail panel.
 * @event ws-inv-facets/nested-panel-closed
 * @payload undefined — no payload; the event is purely a state signal.
 */
export const NESTED_PANEL_CLOSED = 'ws-inv-facets/nested-panel-closed';

/**
 * ws-inv-filters' close-control event. Subscribed by FilterModalButton,
 * which runs hideModal in response (animation → setModalShow(false) →
 * the natural post-state modal-hide emit). Published by this widget's
 * mobile X close icon to dismiss the entire mobile filters modal.
 *
 * Do NOT publish ws-inv-filters/modal-hide instead — that topic is a
 * lifecycle notification ("the modal has been hidden"), not a control
 * signal. Subscribers (this widget's portal teardown, ws-inv-listing's
 * mobile-transition flag) treat it as post-state cleanup. Publishing it
 * synchronously from a click handler runs that cleanup while the modal
 * is still visible — the modal stays open with its contents torn down.
 *
 * @event ws-inv-filters/close-filters
 * @payload undefined — no payload; subscribers re-run hideModal.
 */
export const CLOSE_FILTERS_MODAL = 'ws-inv-filters/close-filters';

/**
 * SSR-safe cross-widget publisher. Uses window.DDC.InvData.publishEvent
 * to match the channel convention used by legitimate emitters in
 * ws-inv-filters (FilterModalButton, VehicleCount). publishEvent is a
 * thin wrapper over DDC.pubsub.publish, so subscribers registered via
 * DDC.pubsub.subscribe still receive these events.
 *
 * No-op when window or InvData.publishEvent is unavailable (Node SSR
 * or early hydration before InvData is wired). Optional chaining
 * provides defense-in-depth against partial DDC initialization.
 *
 * @param {string} topic — one of the exported topic constants
 * @param {*} [payload] — optional payload; omitted from the publishEvent
 *   call when undefined so consumers see exactly the documented arity
 */
export const safePublish = (topic, payload) => {
	if (typeof window === 'undefined') {
		return;
	}
	// Invoke as a method of InvData to preserve `this` binding — DDC's
	// underlying pubsub reads `this` internally (e.g.
	// `this._listenerHasTopic(topic)`), so detaching the reference and
	// calling it as a free function throws at runtime.
	const invData = window?.DDC?.InvData;
	if (typeof invData?.publishEvent !== 'function') {
		return;
	}
	if (payload === undefined) {
		invData.publishEvent(topic);
	} else {
		invData.publishEvent(topic, payload);
	}
};
